import React, { useCallback, useEffect, useMemo, useState } from "react";

import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faArrowDown,
  faArrowDownArrowUp,
  faArrowUp,
  faBan,
  faCheck,
  faCircleNotch,
  faExclamationCircle,
  faEye,
  faFilter,
  faMagnifyingGlass,
  faPause,
  faRefresh,
  faRobot,
  faTimes,
  faTrashCan,
  faTriangleExclamation,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import { faFilter as faFilterSolid } from "@fortawesome/pro-solid-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";
import { endOfDay, startOfDay, subDays } from "date-fns";
import { isEqual, times } from "lodash-es";
import { DateRange } from "react-day-picker";
import { Link, useSearchParams } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { ExecutionLogView } from "src/agents/AgentVersionPage/AgentPlayground";
import type { PlaygroundSessionState } from "src/agents/AgentVersionPage/PlaygroundContext";
import { AgentFailedBanner } from "src/agents/base/AgentFailedBanner";
import { AgentTraceSkeleton } from "src/agents/base/AgentTraceSkeleton";
import { SessionActions } from "src/agents/base/SessionActions";
import {
  DurationPill,
  formatTokenCount,
  TokensPill,
} from "src/agents/MetricPills";
import {
  useAgentSession,
  useSessionToPlaygroundState,
} from "src/agents/queries/execution";
import { useAgentHistory } from "src/agents/queries/history";
import {
  useAgentModels,
  useAgentVersions,
} from "src/agents/queries/management";
import { DecisionEnvironment } from "src/api/types";
import { Button } from "src/base-components/Button";
import { Divider } from "src/base-components/Divider";
import { EnvironmentPill } from "src/base-components/EnvironmentPill";
import { Pill, PillVariants } from "src/base-components/Pill";
import { CustomPopover } from "src/base-components/Popover";
import {
  ResizableSidepaneProvider,
  ResizeHandle,
  useResizableSidepane,
} from "src/base-components/ResizableSidepane";
import { Option, Select } from "src/base-components/Select";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { Summary } from "src/base-components/Summary";
import {
  Cell,
  ColumnVisibilityDropdown,
  Header,
  TableComp,
} from "src/base-components/Table";
import { TruncatedIdPill } from "src/base-components/TruncatedIdPill";
import type {
  AgentHistoryAggregateMetrics,
  ModelProvider,
  SessionListItem,
  SessionResponse,
  SortField,
  Trigger,
} from "src/clients/agent-management";
import { SessionStatus } from "src/clients/agent-management";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon, IconButton } from "src/design-system/Icon";
import { SubHeader } from "src/flow/SubHeader";
import { ModelIcon } from "src/genai/ModelSelector";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import { SubHeaderButton } from "src/layout/SubHeader";
import { AgentHistoryKeys } from "src/router/SearchParams";
import {
  AgentVersionsPageParams,
  getUrlToAgentVersionPage,
  getUrlToHistoricDecisionTrace,
} from "src/router/urls";
import { formatDate } from "src/utils/datetime";
import { formatDuration } from "src/utils/numbers";
import { useTimeWindow } from "src/utils/timeWindow";
import { useParamsDecode } from "src/utils/useParamsDecode";

// ---------------------------------------------------------------------------
// Constants & Mappings
// ---------------------------------------------------------------------------

const PAGE_SIZE = 100;

/**
 * Formats a USD cost value with appropriate precision.
 * For costs >= $0.01, shows 2 decimal places (e.g. "$1.23").
 * For smaller non-zero costs, shows up to 4 decimal places (e.g. "$0.0012").
 */
const formatCost = (cost: number): string => {
  if (cost === 0) return "$0.00";
  if (cost >= 0.01) return `$${cost.toFixed(2)}`;
  return `$${cost.toFixed(4)}`;
};

type ModelDisplayInfo = { displayName: string; provider: ModelProvider };

const STATUS_CONFIG: Record<
  SessionStatus,
  { variant: PillVariants; icon: IconDefinition; label: string }
> = {
  success: { variant: "green", icon: faCheck, label: "Successful" },
  failed: { variant: "red", icon: faTriangleExclamation, label: "Failed" },
  // Running and pending are displayed in the same way to the user
  // the difference is only internal
  running: { variant: "indigo-light", icon: faCircleNotch, label: "Running" },
  pending: { variant: "indigo-light", icon: faCircleNotch, label: "Running" },
  paused: { variant: "yellow", icon: faPause, label: "Waiting for tools" },
  cancelled: { variant: "gray", icon: faBan, label: "Cancelled" },
};

const getDecisionUrlFromTrigger = (
  trigger: Trigger | undefined,
  orgId: string,
  wsId: string,
): string | null => {
  const flowContext =
    trigger?.source_type === "flow" ? trigger.flow_context : null;

  return flowContext?.flow_id &&
    flowContext.flow_version_id &&
    flowContext.decision_id
    ? getUrlToHistoricDecisionTrace({
        orgId,
        wsId,
        flowId: flowContext.flow_id as string,
        versionId: flowContext.flow_version_id as string,
        decisionId: flowContext.decision_id,
        decisionEnv:
          flowContext.environment === "sandbox"
            ? DecisionEnvironment.SANDBOX
            : DecisionEnvironment.LIVE,
      })
    : null;
};

type InvocationSourceType = Trigger["source_type"];

const ORIGIN_LABEL_MAP: Record<InvocationSourceType, string> = {
  api: "API",
  case: "Case",
  flow: "Decision Flow",
  playground: "Agent test",
  test_run: "Flow test run",
};

// We filter "pending" out because we map
// selecting "running" to "running" and "pending" internally

const STATUS_FILTER_OPTIONS = Object.values(SessionStatus)
  .filter((s) => s !== SessionStatus.PENDING)
  .map((s) => ({
    key: s,
    value: STATUS_CONFIG[s].label,
  }));

const ORIGIN_FILTER_OPTIONS: { key: InvocationSourceType; value: string }[] =
  Object.entries(ORIGIN_LABEL_MAP)
    .filter(([key]) => key !== "case")
    .map(([key, value]) => ({ key: key as InvocationSourceType, value }));

// ---------------------------------------------------------------------------
// URL Search Params
// ---------------------------------------------------------------------------

const useHistorySearchParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // --- Environment ---
  const envParam = searchParams.get(AgentHistoryKeys.Environment) as
    | DecisionEnvironment
    | undefined;
  const isDefaultEnvironment =
    !envParam || !Object.values(DecisionEnvironment).includes(envParam);
  const environment = isDefaultEnvironment
    ? DecisionEnvironment.LIVE
    : envParam;

  const setEnvironment = useCallback(
    (env: DecisionEnvironment) => {
      setSearchParams((prev) => {
        prev.set(AgentHistoryKeys.Environment, env);
        return prev;
      });
    },
    [setSearchParams],
  );

  // --- Time window ---
  const fromParam = searchParams.get(AgentHistoryKeys.From);
  const toParam = searchParams.get(AgentHistoryKeys.To);
  const initialTimeWindow = useMemo(
    () =>
      fromParam && toParam
        ? {
            from: startOfDay(new Date(fromParam)),
            to: endOfDay(new Date(toParam)),
          }
        : {
            from: startOfDay(subDays(new Date(), 7)),
            to: endOfDay(new Date()),
          },
    [fromParam, toParam],
  );

  const {
    dateRangePickerValue,
    onDateRangePickerChange,
    onDateRangePickerReset,
  } = useTimeWindow(initialTimeWindow, "agent-history-time-window");

  const onTimeWindowChange = useCallback(
    (range?: DateRange) => {
      onDateRangePickerChange(range);
      setSearchParams((prev) => {
        if (range?.from && range?.to) {
          prev.set(AgentHistoryKeys.From, startOfDay(range.from).toISOString());
          prev.set(AgentHistoryKeys.To, endOfDay(range.to).toISOString());
        } else {
          prev.delete(AgentHistoryKeys.From);
          prev.delete(AgentHistoryKeys.To);
        }
        return prev;
      });
    },
    [onDateRangePickerChange, setSearchParams],
  );

  const onTimeWindowReset = useCallback(() => {
    onDateRangePickerReset();
    setSearchParams((prev) => {
      prev.delete(AgentHistoryKeys.From);
      prev.delete(AgentHistoryKeys.To);
      return prev;
    });
  }, [onDateRangePickerReset, setSearchParams]);

  // --- Sort ---
  const sortField =
    (searchParams.get(AgentHistoryKeys.SortField) as SortField) || "started_at";
  const sortDirection =
    (searchParams.get(AgentHistoryKeys.SortDirection) as "asc" | "desc") ||
    "desc";
  const sort = useMemo(
    () => ({ field: sortField, direction: sortDirection }),
    [sortField, sortDirection],
  );

  const setSort = useCallback(
    (newField: SortField) => {
      setSearchParams((prev) => {
        if (newField === sortField) {
          prev.set(
            AgentHistoryKeys.SortDirection,
            sortDirection === "desc" ? "asc" : "desc",
          );
        } else {
          prev.set(AgentHistoryKeys.SortField, newField);
          prev.set(AgentHistoryKeys.SortDirection, "desc");
        }
        return prev;
      });
    },
    [setSearchParams, sortField, sortDirection],
  );

  // --- Filters ---
  const status = searchParams.getAll(
    AgentHistoryKeys.Status,
  ) as SessionStatus[];
  const versionId = searchParams.getAll(AgentHistoryKeys.VersionId);
  const origin = searchParams.getAll(
    AgentHistoryKeys.Origin,
  ) as InvocationSourceType[];
  const model = searchParams.getAll(AgentHistoryKeys.Model);
  const hasActivePopoverFilters =
    status.length > 0 ||
    versionId.length > 0 ||
    origin.length > 0 ||
    model.length > 0;
  const hasAnyActiveFilters =
    hasActivePopoverFilters || !!dateRangePickerValue?.from;

  const applyFilters = useCallback(
    (values: FilterValues) => {
      setSearchParams((prev) => {
        const setArrayParam = (key: string, arr?: string[]) => {
          prev.delete(key);
          arr?.forEach((v) => prev.append(key, v));
        };
        setArrayParam(AgentHistoryKeys.Status, values.status);
        setArrayParam(AgentHistoryKeys.VersionId, values.versionId);
        setArrayParam(AgentHistoryKeys.Origin, values.origin);
        setArrayParam(AgentHistoryKeys.Model, values.model);
        return prev;
      });
    },
    [setSearchParams],
  );

  // --- Search query ---
  const searchQuery = searchParams.get(AgentHistoryKeys.SessionId) || null;

  const setSearchQuery = useCallback(
    (query: string) => {
      const trimmed = query.trim();
      setSearchParams((prev) => {
        if (trimmed.length > 0) {
          prev.set(AgentHistoryKeys.SessionId, trimmed);
        } else {
          prev.delete(AgentHistoryKeys.SessionId);
        }
        return prev;
      });
    },
    [setSearchParams],
  );

  // --- Selected session ---
  const selectedSessionId =
    searchParams.get(AgentHistoryKeys.SelectedSessionId) || null;

  const setSelectedSessionId = useCallback(
    (value: string | null) => {
      setSearchParams((prev) => {
        if (value) prev.set(AgentHistoryKeys.SelectedSessionId, value);
        else prev.delete(AgentHistoryKeys.SelectedSessionId);
        return prev;
      });
    },
    [setSearchParams],
  );

  const clearFilters = useCallback(() => {
    onDateRangePickerReset();
    setSearchParams((prev) => {
      prev.delete(AgentHistoryKeys.Status);
      prev.delete(AgentHistoryKeys.VersionId);
      prev.delete(AgentHistoryKeys.Origin);
      prev.delete(AgentHistoryKeys.Model);
      prev.delete(AgentHistoryKeys.From);
      prev.delete(AgentHistoryKeys.To);
      return prev;
    });
  }, [onDateRangePickerReset, setSearchParams]);

  const filters = useMemo(
    () => ({
      status,
      versionId,
      origin,
      model,
      applyFilters,
      clearAll: clearFilters,
      hasActivePopoverFilters,
      hasAnyActiveFilters,
    }),
    [
      status,
      versionId,
      origin,
      model,
      applyFilters,
      clearFilters,
      hasActivePopoverFilters,
      hasAnyActiveFilters,
    ],
  );

  return {
    environment,
    isDefaultEnvironment,
    setEnvironment,
    dateRangePickerValue,
    onTimeWindowChange,
    onTimeWindowReset,
    sort,
    setSort,
    filters,
    searchQuery,
    setSearchQuery,
    selectedSessionId,
    setSelectedSessionId,
  };
};

// ---------------------------------------------------------------------------
// Page Components
// ---------------------------------------------------------------------------

export const AgentHistoryPage: React.FC = () => {
  useDocumentTitle(["Agent run history"]);

  return (
    <ResizableSidepaneProvider storageKey="agent-history-sidepane-width">
      <AgentHistoryPageContent />
    </ResizableSidepaneProvider>
  );
};

const AgentHistoryPageContent: React.FC = () => {
  const { agentId, orgId, wsId } = useParamsDecode<AgentVersionsPageParams>();
  const { width, isResizing } = useResizableSidepane();
  const {
    environment,
    isDefaultEnvironment,
    setEnvironment,
    dateRangePickerValue,
    onTimeWindowChange,
    onTimeWindowReset,
    sort,
    setSort,
    filters,
    searchQuery,
    setSearchQuery,
    selectedSessionId,
    setSelectedSessionId,
  } = useHistorySearchParams();

  // Fetch versions for the version column
  const {
    data: versions,
    isLoading: isLoadingVersions,
    isError: isVersionsError,
  } = useAgentVersions(agentId);

  const versionMap = useMemo(
    () => new Map(versions?.map((v) => [v.id, v]) ?? []),
    [versions],
  );

  // Fetch agent models for display name resolution in the table and detail panel
  const {
    data: agentModels,
    isLoading: isLoadingModels,
    isError: isModelsError,
  } = useAgentModels();
  const modelDisplayNameMap = useMemo(
    () =>
      new Map(
        agentModels?.map((m) => [
          m.model_id,
          { displayName: m.display_name, provider: m.provider },
        ]) ?? [],
      ),
    [agentModels],
  );

  // Build filter options for the popover
  const statusSelectOptions: Option[] = useMemo(
    () =>
      STATUS_FILTER_OPTIONS.map((s) => ({
        key: s.key,
        value: s.value,
        icon: STATUS_CONFIG[s.key].icon,
        pillVariant: STATUS_CONFIG[s.key].variant,
      })),
    [],
  );

  const versionSelectOptions: Option[] = useMemo(
    () =>
      versions?.map((v) => ({
        key: v.id,
        value: v.name,
      })) ?? [],
    [versions],
  );

  const originSelectOptions: Option[] = useMemo(
    () =>
      ORIGIN_FILTER_OPTIONS.map((o) => ({
        key: o.key,
        value: o.value,
      })),
    [],
  );

  const modelSelectOptions: Option[] = useMemo(
    () =>
      agentModels?.map((m) => ({
        key: m.model_id,
        value: (
          <div className="flex items-center gap-1.5">
            <ModelIcon modelName={m.display_name} provider={m.provider} />
            <span className="truncate">{m.display_name}</span>
          </div>
        ),
      })) ?? [],
    [agentModels],
  );

  // Build from/to from date picker (default to very wide range for "All time")
  const from = dateRangePickerValue?.from
    ? startOfDay(dateRangePickerValue.from).toISOString()
    : new Date(0).toISOString();
  const to = dateRangePickerValue?.to
    ? endOfDay(dateRangePickerValue.to).toISOString()
    : endOfDay(new Date()).toISOString();

  const {
    data: historyData,
    isLoading: isLoadingHistory,
    isError: isHistoryError,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
    isRefetching,
  } = useAgentHistory({
    agentId,
    environment,
    from,
    to,
    sort,
    // Selecting running also includes pending internally
    status: filters.status.flatMap((s) =>
      s === SessionStatus.RUNNING
        ? [SessionStatus.RUNNING, SessionStatus.PENDING]
        : [s],
    ),
    agent_version_id: filters.versionId,
    origin: filters.origin,
    model: filters.model,
    page_size: PAGE_SIZE,
    searchSessionId: searchQuery,
  });

  const isLoading = isLoadingHistory || isLoadingVersions || isLoadingModels;
  const isError = isHistoryError || isVersionsError || isModelsError;
  const sessions =
    !isError && historyData?.sessions ? historyData.sessions : [];
  const metrics = historyData?.metrics;
  const totalCount = historyData?.total_count;
  const hasData = !isLoading && sessions.length > 0;
  const isEmpty = !isLoading && !isError && !hasData;

  // Default to sandbox when there are no published versions (e.g. new agents
  // where all runs are sandbox/playground runs). Only applies when no explicit
  // environment was chosen via URL.
  useEffect(() => {
    if (
      isDefaultEnvironment &&
      versions &&
      !versions.some((v) => v.status === "published")
    ) {
      setEnvironment(DecisionEnvironment.SANDBOX);
    }
  }, [isDefaultEnvironment, versions, setEnvironment]);

  const columns = useMemo(
    () =>
      buildColumns({
        versionMap,
        modelDisplayNameMap,
        sort,
        onSort: setSort,
        agentId,
        orgId,
        wsId,
      }),
    [versionMap, modelDisplayNameMap, sort, setSort, agentId, orgId, wsId],
  );

  return (
    <div
      className={twJoin(
        "relative flex h-full w-full",
        isResizing && "cursor-col-resize select-none",
      )}
      data-loc="agent-history-page"
    >
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <SubHeader
          title="Agent run history"
          titleAction={
            <EnvironmentPill value={environment} onChange={setEnvironment} />
          }
        >
          <SubHeader.SearchBox
            dataLoc="agent-history-search"
            defaultValue={searchQuery ?? ""}
            placeholder="Search by run ID"
            onChange={setSearchQuery}
          />
          <SubHeader.DatePicker
            placeholder="All time"
            value={dateRangePickerValue}
            resetable
            onChange={onTimeWindowChange}
            onReset={onTimeWindowReset}
          />
          <AgentHistoryFilters
            currentFilters={{
              status: filters.status,
              versionId: filters.versionId,
              origin: filters.origin,
              model: filters.model,
            }}
            disabled={isLoading}
            hasActiveFilters={filters.hasActivePopoverFilters}
            modelOptions={modelSelectOptions}
            originOptions={originSelectOptions}
            statusOptions={statusSelectOptions}
            versionOptions={versionSelectOptions}
            onApply={filters.applyFilters}
          />
          <SubHeader.Button
            dataLoc="agent-history-refresh"
            disabled={isRefetching}
            icon={faRefresh}
            tooltip="Refresh"
            onClick={() => refetch()}
          />
        </SubHeader>
        <div className="flex flex-1 flex-col gap-4 overflow-hidden p-3 pb-0">
          {!searchQuery && (
            <AgentHistoryMetrics
              isLoading={isLoading}
              metrics={metrics}
              totalCount={totalCount}
            />
          )}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
            <TableComp
              columnVisibility={{ origin: false, cost: false }}
              columns={columns}
              data={sessions}
              dataLoc="agent-history-table"
              frameClassName={twJoin("pl-6", (isError || isEmpty) && "h-auto")}
              getRowId={(row) => row.session_id}
              isFetchingNextPage={isFetchingNextPage}
              isLoading={isLoading}
              rowClassName="cursor-pointer hover:bg-gray-50"
              rowPropsGetter={(row) => ({
                "data-loc": `agent-history-row-${row.id}`,
                onClick: () => setSelectedSessionId(row.original.session_id),
                classNameOverrides:
                  row.original.session_id === selectedSessionId
                    ? "bg-gray-50 border-l-4 border-l-indigo-500"
                    : undefined,
              })}
              uiStoreKey="agent-history-columns"
              onScrolledToTheBottom={fetchNextPage}
            />
            {isError && (
              <div className="flex h-full items-center justify-center">
                <EmptyState
                  dataLoc="agent-history-error"
                  description="We're having trouble loading runs. Please try again."
                  headline="Error loading runs"
                  icon={faExclamationCircle}
                  variant="error"
                />
              </div>
            )}
            {isEmpty && (
              <div className="flex h-full items-center justify-center">
                <AgentHistoryEmptyState
                  filters={filters}
                  searchQuery={searchQuery}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedSessionId && (
        <div
          className="relative h-full border-gray-200 border-l bg-white"
          data-loc="run-detail-panel"
          style={{ width: `${width}px` }}
        >
          <ResizeHandle />
          <RunDetailPanel
            modelDisplayNameMap={modelDisplayNameMap}
            sessionId={selectedSessionId}
            versionMap={versionMap}
            onClose={() => setSelectedSessionId(null)}
          />
        </div>
      )}
    </div>
  );
};

const AgentHistoryEmptyState: React.FC<{
  searchQuery: string | null;
  filters: { hasAnyActiveFilters: boolean; clearAll: () => void };
}> = ({ searchQuery, filters }) => {
  if (searchQuery) {
    return (
      <EmptyState
        dataLoc="agent-history-search-empty"
        description="No run found with this ID. Check the ID and try again."
        headline="No run found"
        icon={faMagnifyingGlass}
      />
    );
  }

  if (filters.hasAnyActiveFilters) {
    return (
      <EmptyState
        actionButton={{
          text: "Clear filters",
          onClick: filters.clearAll,
        }}
        dataLoc="agent-history-empty-filtered"
        description="No runs match your current filters."
        headline="No runs found"
        icon={faMagnifyingGlass}
      />
    );
  }

  return (
    <EmptyState
      dataLoc="agent-history-empty"
      description="No Agent runs found for the selected time period."
      headline="No runs found"
      icon={faMagnifyingGlass}
    />
  );
};

const AgentHistoryMetrics: React.FC<{
  isLoading: boolean;
  metrics: AgentHistoryAggregateMetrics | undefined;
  totalCount: number | undefined;
}> = ({ isLoading, metrics, totalCount }) => {
  return (
    <Summary dataLoc="agent-history-metrics" variant="simple">
      {isLoading || !metrics ? (
        times(4, (i) => (
          <div key={i} className="min-w-0 flex-1 px-5 py-4">
            <SkeletonPlaceholder height="h-5" width="w-16" />
            <div className="mt-3">
              <SkeletonPlaceholder height="h-4" width="w-32" />
            </div>
          </div>
        ))
      ) : (
        <>
          <div className="min-w-0 flex-1 px-5 py-4">
            <h2 className="font-inter-semibold-16px text-gray-800">
              {totalCount ?? 0}
            </h2>
            <span className="font-inter-normal-13px text-gray-500">
              Total runs
            </span>
          </div>
          <div className="min-w-0 flex-1 px-5 py-4">
            <h2 className="font-inter-semibold-16px text-gray-800">
              {formatTokenCount(metrics.avg_tokens ?? 0)}
            </h2>
            <span className="font-inter-normal-13px text-gray-500">
              Avg. tokens per run
            </span>
          </div>
          <div className="min-w-0 flex-1 px-5 py-4">
            <h2 className="font-inter-semibold-16px text-gray-800">
              {formatDuration(metrics.avg_duration_ms ?? 0)}
            </h2>
            <span className="font-inter-normal-13px text-gray-500">
              Avg. duration per run
            </span>
          </div>
          <div className="min-w-0 flex-1 px-5 py-4">
            <h2 className="font-inter-semibold-16px text-gray-800">
              {formatCost(metrics.avg_cost_usd ?? 0)}
            </h2>
            <span className="font-inter-normal-13px text-gray-500">
              Avg. cost per run
            </span>
          </div>
        </>
      )}
    </Summary>
  );
};

// ---------------------------------------------------------------------------
// Table Column Definitions
// ---------------------------------------------------------------------------

const columnHelper = createColumnHelper<SessionListItem>();

type ColumnProps = {
  versionMap: Map<string, { name: string; status: string }>;
  modelDisplayNameMap: Map<string, ModelDisplayInfo>;
  sort: { field: SortField; direction: "asc" | "desc" };
  onSort: (field: SortField) => void;
  agentId: string;
  orgId: string;
  wsId: string;
};

const buildColumns = (props: ColumnProps) => [
  columnHelper.accessor("session_id", {
    meta: { displayName: "Run ID" },
    header: (info) => (
      <Header dataLoc="agent-history-header-run-id" info={info}>
        Run ID
      </Header>
    ),
    cell: (info) => (
      <Cell info={info}>
        <div className="w-28">
          <TruncatedIdPill feedback="inline" id={info.getValue()} />
        </div>
      </Cell>
    ),
    size: 120,
  }),

  columnHelper.accessor("started_at", {
    meta: { displayName: "Start time" },
    header: (info) => (
      <Header dataLoc="agent-history-header-start-time" info={info}>
        <SortableColumnHeader
          currentDirection={props.sort.direction}
          currentField={props.sort.field}
          field="started_at"
          label="Start time"
          onSort={props.onSort}
        />
      </Header>
    ),
    cell: (info) => (
      <Cell info={info}>
        <span className="whitespace-nowrap">
          {formatDate(info.getValue(), "MMM d, yyyy 'at' h:mm a")}
        </span>
      </Cell>
    ),
    size: 250,
  }),

  columnHelper.accessor("status", {
    meta: { displayName: "Status" },
    header: (info) => (
      <Header dataLoc="agent-history-header-status" info={info}>
        Status
      </Header>
    ),
    cell: (info) => (
      <Cell info={info}>
        <StatusPill status={info.getValue()} />
      </Cell>
    ),
    size: 200,
  }),

  columnHelper.accessor((row) => row.trigger.source_type, {
    id: "origin",
    meta: { displayName: "Origin" },
    header: (info) => (
      <Header dataLoc="agent-history-header-origin" info={info}>
        Origin
      </Header>
    ),
    cell: (info) => {
      const origin = info.getValue();
      const trigger = info.row.original.trigger;
      const decisionUrl = getDecisionUrlFromTrigger(
        trigger,
        props.orgId,
        props.wsId,
      );

      const pill = (
        <Pill variant="outlined-white" maxWidth>
          <Pill.Text>{ORIGIN_LABEL_MAP[origin]}</Pill.Text>
        </Pill>
      );

      return (
        <Cell info={info}>
          {decisionUrl ? (
            <a
              className="group/link"
              href={decisionUrl}
              rel="noreferrer"
              target="_blank"
              onClick={(e) => e.stopPropagation()}
            >
              {pill}
            </a>
          ) : (
            pill
          )}
        </Cell>
      );
    },
    size: 180,
  }),

  columnHelper.accessor((row) => row.metrics.usage.total_tokens, {
    id: "tokens",
    meta: { displayName: "Tokens" },
    header: (info) => (
      <Header dataLoc="agent-history-header-tokens" info={info}>
        <SortableColumnHeader
          currentDirection={props.sort.direction}
          currentField={props.sort.field}
          field="tokens"
          label="Tokens"
          onSort={props.onSort}
        />
      </Header>
    ),
    cell: (info) => {
      const tokens = info.getValue();
      return (
        <Cell info={info}>
          {tokens != null ? <TokensPill count={tokens} /> : "-"}
        </Cell>
      );
    },
    size: 150,
  }),

  columnHelper.accessor((row) => row.metrics.usage.total_cost_usd, {
    id: "cost",
    meta: { displayName: "Cost" },
    header: (info) => (
      <Header dataLoc="agent-history-header-cost" info={info}>
        Cost
      </Header>
    ),
    cell: (info) => {
      const cost = info.getValue();
      return <Cell info={info}>{cost != null ? formatCost(cost) : "-"}</Cell>;
    },
    size: 100,
  }),

  columnHelper.accessor("model", {
    meta: { displayName: "Model" },
    header: (info) => (
      <Header dataLoc="agent-history-header-model" info={info}>
        Model
      </Header>
    ),
    cell: (info) => {
      const modelId = info.getValue();
      const modelInfo = modelId
        ? props.modelDisplayNameMap.get(modelId)
        : undefined;
      const displayName = modelInfo?.displayName ?? modelId;
      return (
        <Cell info={info}>
          {displayName ? (
            <div className="flex items-center gap-1.5">
              {modelInfo && (
                <ModelIcon
                  modelName={modelInfo.displayName}
                  provider={modelInfo.provider}
                />
              )}
              <span className="truncate">{displayName}</span>
            </div>
          ) : (
            "-"
          )}
        </Cell>
      );
    },
    size: 180,
  }),

  columnHelper.accessor("agent_version_id", {
    meta: { displayName: "Version" },
    header: (info) => (
      <Header dataLoc="agent-history-header-version" info={info}>
        Version
      </Header>
    ),
    cell: (info) => {
      const versionId = info.getValue();
      const version = props.versionMap.get(versionId);
      const versionUrl = getUrlToAgentVersionPage(
        props.orgId,
        props.wsId,
        props.agentId,
        versionId,
      );
      return (
        <Cell info={info}>
          {version ? (
            <Link
              to={versionUrl}
              className="truncate hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {version.name}
            </Link>
          ) : (
            <span className="truncate text-gray-400">{versionId}</span>
          )}
        </Cell>
      );
    },
    size: 180,
  }),

  columnHelper.accessor((row) => row.metrics.execution.session_duration_ms, {
    id: "duration",
    meta: { displayName: "Duration" },
    header: (info) => (
      <Header dataLoc="agent-history-header-duration" info={info}>
        <SortableColumnHeader
          currentDirection={props.sort.direction}
          currentField={props.sort.field}
          field="duration"
          label="Duration"
          onSort={props.onSort}
        />
      </Header>
    ),
    cell: (info) => {
      const ms = info.getValue();
      return (
        <Cell info={info}>
          {ms != null ? <DurationPill durationMs={ms} /> : "-"}
        </Cell>
      );
    },
    size: 110,
  }),

  columnHelper.display({
    id: "table-view-options",
    meta: { hideSkeleton: true },
    header: (info) => (
      <div className="flex w-13.5 justify-end bg-white pr-1.5">
        <ColumnVisibilityDropdown min={3} table={info.table} />
      </div>
    ),
    cell: () => (
      <span className="flex justify-end pr-1.5 text-gray-500 opacity-0 group-hover/row:opacity-100">
        <Icon icon={faEye} size="sm" />
      </span>
    ),
    minSize: 40,
    maxSize: 40,
  }),
];

const SortableColumnHeader: React.FC<{
  label: string;
  field: SortField;
  currentField: SortField;
  currentDirection: "asc" | "desc";
  onSort: (field: SortField) => void;
}> = ({ label, field, currentField, currentDirection, onSort }) => {
  const isActive = currentField === field;
  const getSortIcon = () => {
    if (!isActive) return faArrowDownArrowUp;
    return currentDirection === "asc" ? faArrowUp : faArrowDown;
  };
  const icon = getSortIcon();

  return (
    <button
      className="flex items-center gap-1"
      data-loc={`agent-history-sort-${field}`}
      onClick={() => onSort(field)}
    >
      {label}
      <Icon icon={icon} size="2xs" variant={isActive ? "hover" : "default"} />
    </button>
  );
};

const RunDetailPanel: React.FC<{
  modelDisplayNameMap: Map<string, ModelDisplayInfo>;
  sessionId: string;
  versionMap: Map<string, { name: string; status: string }>;
  onClose: () => void;
}> = ({ modelDisplayNameMap, sessionId, versionMap, onClose }) => {
  const sessionQuery = useAgentSession(sessionId, { polling: true });
  const session = sessionQuery.data;

  const sessionState = useSessionToPlaygroundState(session, sessionId);

  const version = session?.agent_version_id
    ? versionMap.get(session.agent_version_id)
    : undefined;

  return (
    <div className="flex h-full flex-col" data-loc="run-detail-panel-content">
      <RunDetailPanelHeader
        rawSessionData={session ?? null}
        sessionId={sessionId}
        sessionStatus={session?.status}
        trigger={session?.trigger}
        version={version}
        onClose={onClose}
      />
      <RunDetailMetrics
        isLoading={sessionQuery.isLoading}
        modelDisplayNameMap={modelDisplayNameMap}
        session={session}
      />
      <RunDetailBody
        isError={sessionQuery.isError}
        isLoading={sessionQuery.isLoading}
        sessionState={sessionState}
      />
    </div>
  );
};

const RunDetailPanelHeader: React.FC<{
  rawSessionData: SessionResponse | null;
  sessionId: string;
  sessionStatus: SessionStatus | undefined;
  trigger: Trigger | undefined;
  version: { name: string; status: string } | undefined;
  onClose: () => void;
}> = ({
  rawSessionData,
  sessionId,
  sessionStatus,
  trigger,
  version,
  onClose,
}) => {
  const { orgId, wsId } = useParamsDecode<AgentVersionsPageParams>();

  const decisionFlowUrl = getDecisionUrlFromTrigger(trigger, orgId, wsId);

  return (
    <div className="px-5 pt-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <span className="shrink-0 font-inter-semibold-13px text-gray-800">
            Agent run trace
          </span>
          {version && (
            <span className="truncate font-inter-semibold-13px text-gray-800">
              {version.name}
            </span>
          )}
          {sessionStatus && <StatusPill status={sessionStatus} />}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {decisionFlowUrl && (
            <Button
              dataLoc="open-decision-flow"
              iconLeft={faEye}
              size="sm"
              variant="secondary"
              onClick={() => {
                window.open(window.location.origin + decisionFlowUrl, "_blank");
              }}
            >
              Decision flow
            </Button>
          )}
          <SessionActions
            rawSessionData={rawSessionData}
            sessionId={sessionId}
          />
          <IconButton
            dataLoc="close-run-detail"
            icon={faTimes}
            size="xs"
            onClick={onClose}
          />
        </div>
      </div>
      <Divider />
    </div>
  );
};

const RunDetailMetrics: React.FC<{
  isLoading: boolean;
  modelDisplayNameMap: Map<string, ModelDisplayInfo>;
  session: SessionResponse | undefined;
}> = ({ isLoading, modelDisplayNameMap, session }) => {
  const loading = isLoading || !session;
  const metrics = session?.metrics;
  const config = session?.agent_version_config;
  const technicalModelId = config?.model_config?.model;
  const modelInfo = technicalModelId
    ? modelDisplayNameMap.get(technicalModelId)
    : undefined;
  const modelDisplayName = modelInfo?.displayName ?? technicalModelId ?? "-";

  return (
    <div className="px-5 pt-3" data-loc="run-detail-metrics">
      <div className="grid grid-cols-5 divide-x divide-gray-200 rounded-lg border border-gray-200">
        <MetricItem
          isLoading={loading}
          label="Total Tokens"
          value={
            metrics?.usage.total_tokens != null
              ? formatTokenCount(metrics.usage.total_tokens)
              : "-"
          }
        />
        <MetricItem
          isLoading={loading}
          label="Cost"
          value={
            metrics?.usage.total_cost_usd != null
              ? formatCost(metrics.usage.total_cost_usd)
              : "-"
          }
        />
        <MetricItem
          isLoading={loading}
          label="Duration"
          value={
            metrics?.execution.session_duration_ms != null
              ? formatDuration(metrics.execution.session_duration_ms)
              : "-"
          }
        />
        <MetricItem
          isLoading={loading}
          label="Model"
          value={modelDisplayName}
        />
        <MetricItem
          isLoading={loading}
          label="Tools called"
          value={String(metrics?.execution.user_tool_call_count ?? 0)}
        />
      </div>
    </div>
  );
};

const RunDetailBody: React.FC<{
  isLoading: boolean;
  isError: boolean;
  sessionState: PlaygroundSessionState | null;
}> = ({ isLoading, isError, sessionState }) => {
  if (isLoading) {
    return (
      <div className="px-5 pt-4">
        <AgentTraceSkeleton />
      </div>
    );
  }

  if (isError || !sessionState || sessionState.status === "idle") {
    return (
      <div className="flex flex-1 items-center justify-center">
        <EmptyState
          dataLoc="run-detail-error"
          description="Run details could not be loaded."
          headline="Unable to load run"
          icon={faRobot}
          variant="error"
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-5">
      {sessionState.status === "stopped" &&
        sessionState.substatus === "failed" && (
          <AgentFailedBanner
            terminationReason={sessionState.terminationReason}
            terminationMessage={sessionState.terminationMessage}
          />
        )}
      <ExecutionLogView
        key={sessionState.sessionId}
        hideTestInput={false}
        inputTitle="Agent input"
        sessionState={sessionState}
      />
    </div>
  );
};

const MetricItem: React.FC<{
  label: string;
  value: string;
  isLoading?: boolean;
}> = ({ label, value, isLoading }) => (
  <div className="min-w-0 px-4 py-3">
    {isLoading ? (
      <>
        <SkeletonPlaceholder height="h-5" width="w-10" />
        <div className="mt-1">
          <SkeletonPlaceholder height="h-4.5" width="w-16" />
        </div>
      </>
    ) : (
      <>
        <div className="truncate font-inter-semibold-13px text-gray-800">
          {value}
        </div>
        <div className="font-inter-normal-12px text-gray-500">{label}</div>
      </>
    )}
  </div>
);

const StatusPill: React.FC<{
  status: SessionStatus;
  spinIfLoading?: boolean;
}> = ({ status, spinIfLoading = true }) => {
  const config = STATUS_CONFIG[status];
  return (
    <Pill size="sm" variant={config.variant} maxWidth>
      <Pill.Icon
        icon={config.icon}
        spin={
          spinIfLoading ? status === "running" || status === "pending" : false
        }
      />
      <Pill.Text>{config.label}</Pill.Text>
    </Pill>
  );
};

// ---------------------------------------------------------------------------
// Additional Filters Popover
// ---------------------------------------------------------------------------

type FilterValues = {
  status?: SessionStatus[];
  versionId?: string[];
  origin?: InvocationSourceType[];
  model?: string[];
};

type AgentHistoryFiltersProps = {
  currentFilters: FilterValues;
  disabled?: boolean;
  hasActiveFilters: boolean;
  onApply: (values: FilterValues) => void;
  statusOptions: Option[];
  versionOptions: Option[];
  originOptions: Option[];
  modelOptions: Option[];
};

const AgentHistoryFilters: React.FC<AgentHistoryFiltersProps> = ({
  currentFilters,
  disabled,
  hasActiveFilters,
  onApply,
  statusOptions,
  versionOptions,
  originOptions,
  modelOptions,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<FilterValues>(currentFilters);

  const handleOpen = useCallback(() => {
    setDraft(currentFilters);
    setIsOpen(true);
  }, [currentFilters]);

  const handleClear = () => {
    onApply({});
    setDraft({});
  };

  const isActive = isOpen || hasActiveFilters;

  const removeUndefinedFromObject = (obj: Record<string, any>) => {
    return Object.fromEntries(
      Object.entries(obj).filter(([_, value]) => value !== undefined),
    );
  };

  const draftStateIsDifferentThanCurrent = !isEqual(
    removeUndefinedFromObject(draft),
    removeUndefinedFromObject(currentFilters),
  );

  return (
    <CustomPopover
      button={
        <SubHeaderButton
          dataLoc="agent-history-filters-button"
          disabled={disabled}
          icon={isActive ? faFilterSolid : faFilter}
          tooltip="Additional filters"
          variant={isActive ? "active" : "default"}
          onClick={() => (isOpen ? setIsOpen(false) : handleOpen())}
        />
      }
      className="w-[450px]"
      dataLoc="agent-history-filters"
      isOpen={isOpen}
      placement="bottom-end"
    >
      <div className="divide-y divide-gray-200">
        <div className="flex items-center justify-between px-4 py-3">
          <span className="font-inter-normal-13px text-gray-800">
            Additional filters
          </span>
          <IconButton
            dataLoc="agent-history-filters-close"
            icon={faXmark}
            size="xs"
            onClick={() => setIsOpen(false)}
          />
        </div>

        <div className="flex flex-col gap-4 p-4">
          <FilterRow label="Run status">
            <Select
              dataLoc="agent-history-filter-status"
              multiple
              options={statusOptions}
              placeholder="All statuses"
              value={draft.status ?? []}
              onChange={(value) =>
                setDraft((d) => ({
                  ...d,
                  status: value as SessionStatus[],
                }))
              }
            />
          </FilterRow>

          <FilterRow label="Agent version">
            <Select
              dataLoc="agent-history-filter-version"
              multiple
              options={versionOptions}
              placeholder="All versions"
              value={draft.versionId ?? []}
              onChange={(value) =>
                setDraft((d) => ({ ...d, versionId: value }))
              }
            />
          </FilterRow>

          <FilterRow label="Origin">
            <Select
              dataLoc="agent-history-filter-origin"
              multiple
              options={originOptions}
              placeholder="All origins"
              value={draft.origin ?? []}
              onChange={(value) =>
                setDraft((d) => ({
                  ...d,
                  origin: value as InvocationSourceType[],
                }))
              }
            />
          </FilterRow>

          <FilterRow label="Model">
            <Select
              dataLoc="agent-history-filter-model"
              multiple
              options={modelOptions}
              placeholder="All models"
              value={draft.model ?? []}
              onChange={(value) => setDraft((d) => ({ ...d, model: value }))}
            />
          </FilterRow>
        </div>

        <div className="flex justify-between px-4 pt-4 pb-3">
          <Button
            dataLoc="agent-history-filters-clear"
            iconLeft={faTrashCan}
            size="sm"
            variant="secondary"
            onClick={handleClear}
            disabled={!hasActiveFilters && !draftStateIsDifferentThanCurrent}
          >
            Clear filters
          </Button>
          <Button
            dataLoc="agent-history-filters-apply"
            size="sm"
            variant="primary"
            onClick={() => onApply(draft)}
            disabled={!draftStateIsDifferentThanCurrent}
          >
            Apply
          </Button>
        </div>
      </div>
    </CustomPopover>
  );
};

const FilterRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex items-center justify-between gap-4">
    <span className="shrink-0 font-inter-medium-12px text-gray-700">
      {label}
    </span>
    <div className="w-[200px]">{children}</div>
  </div>
);
